import { articlesOptions, issuesOptions } from '@shared/api';
import type { RouterContext } from './router-context';

/**
 * Home-route *prefetch* for templates whose home page fetches its issue
 * list through the shared `issuesOptions` factory (`@shared/lib/hooks`'s
 * `useIssues()`, called with no args everywhere it's used) -- starts that
 * fetch the instant the route matches instead of waiting for the home
 * chunk to load and mount, the same win `issue`/`article`'s loaders get.
 *
 * Deliberately NOT `await`ed/returned, unlike `issue`/`article`'s loaders.
 * Those routes' entire content *is* the loader's data (and their factories
 * have explicit offline-fallback/redirect handling for when that data
 * can't be had), so blocking navigation on it is correct there. Home is
 * different: it renders real, data-independent UI regardless of whether
 * the issues list has loaded yet -- most visibly the JWT/token-auth login
 * gate, which must still appear even when the network is down or slow.
 * `home`'s hooks are still plain `useQuery` (not `useSuspenseQuery`), so
 * they already tolerate a still-in-flight or failed query fine on their
 * own; blocking the route on it here would only make things worse, not
 * better -- confirmed for real, not just in theory: an earlier version of
 * this file awaited `ensureQueryData`, and `e2e/tests/jwt-login.spec.ts`'s
 * "login error" case (which deliberately never mocks `**\/graphql`, so the
 * request hangs against a nonexistent host) timed out because the login
 * form itself never got to render. `.catch(() => {})` only silences the
 * unhandled-rejection warning this fire-and-forget call would otherwise
 * produce on a real failure -- `useIssues()`'s own `useQuery` still sees
 * and surfaces the same error normally when the component mounts.
 *
 * Deliberately does NOT pass `isOnline`/`ttlMinutes` cache options to
 * `issuesOptions`/`articlesOptions` (unlike a mounted `useQuery` call) --
 * matches `morningstarHomeLoader`'s own prefetch, which never guessed at
 * connectivity either. A loader only has `navigator.onLine` to go on, and
 * that alone isn't trustworthy: it can still read `true` for a moment right
 * after the app boots while genuinely offline (`useIsOnline()` corrects
 * itself asynchronously, via a real reachability check against the GraphQL
 * host, once mounted -- see that hook's own doc comment). Guessing "online"
 * from it here previously set this query's own gcTime/staleTime to the
 * *online* window before that correction landed -- confirmed as the cause
 * of a real device bug (see `queryPersister`'s comment in
 * `shared/query-client/query-client.ts`) once that window also shrank the
 * persisted cache's maxAge; that coupling is gone now, but there's no
 * reason to keep feeding this loader's query the same unverified signal.
 * Omitting cache options here just means `defaultQueryClient`'s own
 * connectivity-agnostic defaults apply until the component's `useIssues()`/
 * `useArticles()` call -- backed by `AppContext`'s `onlineStatus`, itself
 * `useIsOnline()`'s verified value -- takes over moments later with the
 * real numbers.
 */
export function homeIssuesLoader({ context }: { context: RouterContext }) {
	context.queryClient.ensureQueryData(issuesOptions({})).catch(() => {});
}

/**
 * Same as `homeIssuesLoader`, plus the featured-articles prefetch
 * magazine's home page also needs via `useArticles({ isFeatured: true })`
 * -- same non-blocking reasoning applies.
 */
export function homeIssuesAndArticlesLoader({
	context,
}: {
	context: RouterContext;
}) {
	homeIssuesLoader({ context });
	context.queryClient
		.ensureQueryData(articlesOptions({ isFeatured: true }))
		.catch(() => {});
}
